<template>
  <div class="mars-scheduler">
    <mars-scheduler-filter v-model="filter" :showing="showing" />
    <div
      class="d-flex justify-content-between align-items-end mt-3 mb-1"
      style="font-size:19px;"
    >
      <div class="d-flex align-items-center mb-1">
        <vue-toggles
          v-if="currentUser && !currentUser.onlyAccessAssignedPatients"
          @click="changeAssignedToMEToggle()"
          :value="assignedToMe"
          height="20"
          width="30"
          checkedBg="#34C759"
          uncheckedBg="#FF6161"
        />
        <small
          v-if="currentUser && !currentUser.onlyAccessAssignedPatients"
          class="ml-2"
          >Show My Patients only</small
        >
      </div>
      <div v-if="list.length < listTotal" class="d-flex justify-content-center">
        <b-button
          @click="loadMorePatients"
          variant="outline-secondary"
          style="padding: 10px 10px !important; border-radius:0px;"
          >Load more patients</b-button
        >
      </div>
    </div>
    <mars-scheduler-grid
      :list="list"
      :patientAvatars="patientAvatars"
      :dateRange="schedulerDateRange"
      :showAddEditMedication.sync="showAddEditMedication"
      :editMedicationId.sync="editMedicationId"
      @deleteMedication="deleteMedication"
      @renderRows="scrollTop"
    />
    <mars-add-edit-medication-modal
      v-if="showAddEditMedication"
      v-model="showAddEditMedication"
      :medicationId="editMedicationId"
      @onModify="onModifyAddEditMedication"
    />
  </div>
</template>

<script>
import moment from "moment";
import { mapState, mapActions, mapGetters } from "vuex";
import avatarServer from "../../server/avatar";
import eocServer from "../../server/eoc";
import marsServer from "../../server/marsRecord";
import prnMedicationServer from "../../server/prnMedication";
import {
  formatTimeOnly,
  formatDateTimeSql,
  parseNearestTime,
  formatTimeOnlyMysql,
  formatDateOnlyMysql
} from "../../helpers/date";
import MarsSchedulerFilter from "./MarsSchedulerFilter";
import MarsSchedulerGrid from "./MarsSchedulerGrid";
import MarsAddEditMedicationModal from "./MarsAddEditMedicationModal";

export default {
  name: "MarsScheduler",
  components: {
    MarsSchedulerFilter,
    MarsSchedulerGrid,
    MarsAddEditMedicationModal
  },
  props: ["showing", "assignedToMeValue"],
  data() {
    return {
      list: [],
      listTotal: 0,
      filter: {
        eoc: [],
        medication: [],
        marsStatus: [],
        date: formatDateOnlyMysql(),
        startDate: null,
        endDate: null,
        page: 0,
        pageSize: 200,
        orderBy: "datetime",
        orderByDescending: true
      },
      firstLoading: true,
      patientAvatars: {},
      showAddEditMedication: false,
      editMedicationId: null
    };
  },
  mounted() {
    const currentAdjustedDateTime = this.getCurrentAdjustedDateTime();
    this.filter.date = formatDateOnlyMysql(currentAdjustedDateTime);
    this.filter.startDate = moment(currentAdjustedDateTime, "YYYY-MM-DD")
      .startOf("week")
      .startOf("day")
      .toDate();
    this.filter.endDate = moment(currentAdjustedDateTime, "YYYY-MM-DD")
      .endOf("week")
      .endOf("day")
      .toDate();
  },
  methods: {
    ...mapActions("layout", {
      showAlertSuccess: "showAlertSuccess",
      showLoading: "show",
      hideLoading: "hide",
      increaseRequestsPending: "increaseRequestsPending",
      decreaseRequestsPending: "decreaseRequestsPending"
    }),
    async fetchInitialData() {
      this.searchMars(false);
      this.getActiveClients();
    },
    changeAssignedToMEToggle() {
      this.assignedToMe = !this.assignedToMe;
    },
    loadMorePatients() {
      this.filter.page = Math.trunc(this.list.length / this.filter.pageSize);
      this.fetchMars();
    },
    async fetchMars() {
      this.increaseRequestsPending();
      this.showLoading();
      const result = await marsServer.query(
        {
          episodeOfCareIds: this.filter.eoc.map(eoc => eoc.value),
          fromDate: formatDateTimeSql(this.schedulerDateRange.start),
          toDate: formatDateTimeSql(this.schedulerDateRange.end),
          medicationIds: this.filter.medication.map(m => m.value),
          status: this.filter.marsStatus.map(status => status.value),
          page: this.filter.page,
          pageSize: this.filter.pageSize,
          orderBy: "patient",
          orderByDescending: false,
          assignedToMe: this.assignedToMe,
          fromScheduler: true
        },
        { "Hide-Loading": true }
      );

      const scroll = document.querySelector(
        ".mars-scheduler-grid .b-grid-body-container.b-widget-scroller"
      );
      if (scroll) {
        this.lastScrollTop = scroll.scrollTop;
      }

      await this.downloadAvatars(result.records);
      const withoutDuplicates = result.records.filter(
        x => !this.list.find(e => e.id == x.id)
      );
      this.list = [...this.list, ...withoutDuplicates];
      this.listTotal = result.count;
      this.decreaseRequestsPending();
      this.hideLoading();
    },
    searchMars(delayed = true) {
      this.filter.page = 0;
      this.list = [];
      if (delayed) {
        if (this.lastTimeout) {
          clearTimeout(this.lastTimeout);
          this.lastTimeout = null;
        }
        this.lastTimeout = setTimeout(async () => {
          this.fetchMars();
        }, 1000);
      } else {
        this.fetchMars();
      }
    },
    async fetchFormPrnMedications() {
      if (this.formPrn.eoc) {
        const optionsResult = await prnMedicationServer.query(
          this.formPrn.eoc.value,
          {}
        );
        this.formPrnMedications = optionsResult.map(prnMedication => ({
          value: prnMedication.id,
          label: prnMedication.medication.name,
          data: prnMedication
        }));
      } else this.formPrnMedications = [];
    },
    async getActiveClients() {
      const optionsResult = await eocServer.queryList(
        {
          searchPhrase: "",
          eocStatus: ["Active", "Pending"],
          locationId: [],
          page: 1,
          pageSize: 50
        },
        {
          "Hide-Loading": true
        }
      );
      this.activeClients = optionsResult.episodesOfCare.map(eoc => ({
        value: eoc.id,
        label: this.formatEocObj(eoc),
        data: eoc
      }));
    },
    async downloadAvatars(medications) {
      let patientAvatars = { ...this.patientAvatars };
      for (let index = 0; index < medications.length; index++) {
        const medication = medications[index];
        if (
          medication.episodeOfCare.patient.avatarId &&
          !patientAvatars[medication.episodeOfCare.patient.avatarId]
        ) {
          try {
            patientAvatars[
              medication.episodeOfCare.patient.avatarId
            ] = await avatarServer.downloadUrl(
              medication.episodeOfCare.patient.avatarId
            );
          } catch {
            patientAvatars[
              medication.episodeOfCare.patient.avatarId
            ] = require(`../../assets/avatar-example.png`);
          }
          this.patientAvatars = { ...patientAvatars };
        }
      }
    },
    onModifyAddEditMedication(record) {
      this.showAddEditMedication = false;
      let index = this.list.findIndex(m => m.id == record.id);

      if (index >= 0) {
        this.list.splice(index, 1, record);
      } else {
        this.filter.page = 0;
        this.list = [];
        this.fetchMars();
      }
    },
    async deleteMedication(id) {
      this.$dialog
        .confirm(
          {
            title: "Are you sure you want to delete this record?"
          },
          {
            view: "confirm-delete"
          }
        )
        .then(async () => {
          await marsServer.delete(id);
          this.filter.page = 0;
          this.list = [];
          await this.fetchMars();
          this.showAlertSuccess();
        })
        .catch(function() {});
    },
    scrollTop() {
      if (typeof this.lastScrollTop == "number") {
        const scroll = document.querySelector(
          ".mars-scheduler-grid .b-grid-body-container.b-widget-scroller"
        );
        if (scroll) {
          scroll.scrollTop = this.lastScrollTop;
          this.lastScrollTop = null;
        }
      }
    }
  },
  computed: {
    ...mapGetters("layout", ["getCurrentAdjustedDateTime"]),
    ...mapState("layout", {
      currentUser: state => state.currentUser
    }),
    schedulerDateRange: {
      get() {
        return {
          start: moment(this.filter.date, "YYYY-MM-DD")
            .startOf("week")
            .startOf("day")
            .toDate(),
          end: moment(this.filter.date, "YYYY-MM-DD")
            .endOf("week")
            .endOf("day")
            .toDate()
        };
      }
    },
    assignedToMe: {
      get() {
        return this.assignedToMeValue;
      },
      set() {
        this.$emit("change-assignedToMe");
      }
    }
  },
  watch: {
    showing: {
      handler: function(newValue) {
        if (newValue) {
          if (this.firstLoading) {
            const timeFrom = parseNearestTime(
              formatTimeOnlyMysql(
                moment(this.getCurrentAdjustedDateTime())
                  .subtract(1, "hour")
                  .toDate()
              )
            );
            this.filter.timeFrom = {
              value: timeFrom,
              label: formatTimeOnly(timeFrom, "HH:mm")
            };
            setTimeout(() => {
              this.firstLoading = false;
            });
          }
          this.fetchInitialData();
        }
      }
    },
    "filter.eoc": function() {
      this.searchMars();
    },
    "filter.marsStatus": function() {
      this.searchMars();
    },
    "filter.medication": function() {
      this.searchMars();
    },
    "filter.date": function() {
      this.searchMars();
    },
    "formPrn.eoc": function() {
      this.fetchFormPrnMedications();
    },
    assignedToMe: function() {
      this.filter.page = 0;
      this.list = [];
      this.fetchMars();
    }
  }
};
</script>

<style lang="scss">
.mars-scheduler {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 185px);
}
</style>
